import type { AppPlatform } from '../ports';
import { createBrowserExchangePlatform } from './exchange';
import {
  BPROJ_EXTENSION,
  BPROJ_MIME_TYPE,
  BPROJT_EXTENSION,
  BPROJT_MIME_TYPE,
  createBrowserProjectFileGateway,
  DEFAULT_MAX_PROJECT_BYTES,
  readProjectText,
  type BrowserProjectFileGateway,
} from './file-api';
import {
  createWindowLifecycleInstaller,
  type BrowserLifecycleInstaller,
} from './lifecycle';

export interface BrowserProjectSnapshot {
  readonly contents: string;
  readonly suggestedName: string;
}

export interface BrowserProjectController<
  TTemplate,
  TTemplateExportInput,
  TTemplateCreateInput,
> {
  confirmDiscardChanges(action: 'new' | 'open' | 'import'): boolean | Promise<boolean>;
  createNewProject(): void | Promise<void>;
  loadProjectText(contents: string, sourceName: string): void | Promise<void>;
  captureProject(): BrowserProjectSnapshot | Promise<BrowserProjectSnapshot>;
  captureProjectTemplate(
    input: TTemplateExportInput,
  ): BrowserProjectSnapshot | Promise<BrowserProjectSnapshot>;
  createProjectFromTemplate(
    template: TTemplate,
    input: TTemplateCreateInput,
  ): void | Promise<void>;
  isDirty(): boolean;
}

export interface BrowserAppPlatformOptions<
  TTemplate,
  TTemplateExportInput,
  TTemplateCreateInput,
> {
  readonly controller: BrowserProjectController<
    TTemplate,
    TTemplateExportInput,
    TTemplateCreateInput
  >;
  readonly files?: BrowserProjectFileGateway;
  readonly lifecycle?: BrowserLifecycleInstaller;
  readonly maxProjectBytes?: number;
}

/**
 * Browser implementation of the application platform contract.
 *
 * Browsers cannot replace an arbitrary local file without retaining a File
 * System Access handle, so the initial `save` and `saveAs` operations both
 * produce a Blob download. A future gateway can retain a handle without
 * changing this application-level contract.
 */
export function createBrowserAppPlatform<
  TTemplate,
  TTemplateExportInput,
  TTemplateCreateInput,
>(
  options: BrowserAppPlatformOptions<
    TTemplate,
    TTemplateExportInput,
    TTemplateCreateInput
  >,
): AppPlatform<TTemplate, TTemplateExportInput, TTemplateCreateInput> {
  const { controller } = options;
  const files = options.files ?? createBrowserProjectFileGateway();
  const maxProjectBytes = options.maxProjectBytes ?? DEFAULT_MAX_PROJECT_BYTES;

  const downloadSnapshot = async () => {
    const snapshot = await controller.captureProject();
    await files.downloadTextFile({
      contents: snapshot.contents,
      suggestedName: snapshot.suggestedName,
      extension: BPROJ_EXTENSION,
      mimeType: BPROJ_MIME_TYPE,
    });
  };

  return {
    // 交換格式在瀏覽器一律提供，共用工具列因此在 /local 與 cloud 都會顯示。
    ...createBrowserExchangePlatform({
      confirmDiscard: () => controller.confirmDiscardChanges('import'),
    }),

    async new() {
      if (!(await controller.confirmDiscardChanges('new'))) return false;
      await controller.createNewProject();
      return true;
    },

    async open() {
      if (!(await controller.confirmDiscardChanges('open'))) return false;

      const file = await files.pickProjectFile();
      if (!file) return false;

      const contents = await readProjectText(file, maxProjectBytes);
      await controller.loadProjectText(contents, file.name);
      return true;
    },

    save: downloadSnapshot,
    saveAs: downloadSnapshot,

    async exportTemplate(input) {
      const snapshot = await controller.captureProjectTemplate(input);
      await files.downloadTextFile({
        contents: snapshot.contents,
        suggestedName: snapshot.suggestedName,
        extension: BPROJT_EXTENSION,
        mimeType: BPROJT_MIME_TYPE,
      });
    },

    async createFromTemplate(template, input) {
      await controller.createProjectFromTemplate(template, input);
    },

    installLifecycle() {
      const lifecycle = options.lifecycle ?? createWindowLifecycleInstaller(window);
      return lifecycle.install(() => controller.isDirty());
    },
  };
}
